import React, { FC, useContext, useEffect, useState } from 'react'

import { sumBy } from 'lodash'

import MainModal from './common/MainModal'
import { AddItemSkeleton } from './common/AddItemSkeleton'
import { AddItemAccordion } from './common/AddItemAccordion'

import { HandleModalContext } from '../../layout/mainlayout/MainLayout'

import helpers from '../../commonHelpers/helpers'
import { convertPrice, getFloatPrice } from '../../helpers/price'
import { getConvertedData } from '../../models/interface.helper'
import { getFeesTabData } from '../../helpers/fees'
import { getUserFullName } from '../../helpers/helpers'
import { httpService } from '../../services/httpService'
import useToasterHelper from '../../helpers/ToasterHelper'

import FirestoreService from '../../services/firestoreService'
import EventReviewPublishService from '../../services/firebase-service/event-review-publish/EventReviewPublishService'
import EventRegisteredUsersService from '../../services/firebase-service/event-registered-users/EventRegisteredUsersService'

import { RegistrationFeesModel } from '../../models/registration-fees/registrationFees.model'

import {
  IFeesTab,
  TEventRegisteredUsers,
} from '../../models/event-registered-users/event-registered-users.interface'
import { IUserInterface } from '../../models/users/user.interface'
import { IRegistrationFeesInterface } from '../../models/registration-fees/registrationFees.interface'
import { IEventReviewPublish } from '../../models/event-review-publish/event-review-publish.interface'

import { MODAL_CONSTS } from '../../const/modal-const'
import { CONST } from '../../const/const'
import { FEES_CATEGORY_CONST } from '../events/views/details/EventDetailsViewComponentFees'
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../types/payment'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface AddFeesProps {
  dataToPassOn: {
    eventId: string
    registrationDocId: string
    user: IUserInterface
    onSave?: (value: IRegistrationFeesInterface[]) => void
  }
}

const getByCategory = (fees: (IFeesTab & { selectedCount?: number })[], category: string) => {
  return fees
    .filter((fee) => fee.feesCategory === category)
    .map((fee) => ({
      id: fee.feesItemId,
      name: fee.feesTitle || '',
      cost: fee.feesPrice || 0,
      available: fee.available,
      selectedCount: fee.selectedCount || 0,
    }))
}

export const AddFees: FC<AddFeesProps> = ({ dataToPassOn }) => {
  const toastFunctions = useToasterHelper()

  const { eventId, registrationDocId, user, onSave } = dataToPassOn

  const [buttonLoading, setButtonLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fees, setFees] = useState<(IFeesTab & { selectedCount?: number })[]>([])
  const [selectedFees, setSelectedFees] = useState<(IFeesTab & { selectedCount?: number })[]>([])
  const [registeredUser, setRegisteredUser] = useState<TEventRegisteredUsers | null>(null)
  const [publishedEvent, setPublishedEvent] = useState<IEventReviewPublish | null>(null)

  const handleModal = useContext(HandleModalContext)?.handleModal

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true)

        const publishedEventInDb = await EventReviewPublishService.getById(eventId)
        const registrationInDb = await EventRegisteredUsersService.getById(registrationDocId)

        const feesTabData = getFeesTabData(publishedEventInDb)

        setFees(feesTabData)
        setRegisteredUser(registrationInDb)
        setPublishedEvent(publishedEventInDb)
      } catch (e) {
      } finally {
        setLoading(false)
      }
    }
    getData().then()
  }, [eventId, registrationDocId])

  const onSubmit = async () => {
    const feesToCreate: IRegistrationFeesInterface[] = []

    try {
      setButtonLoading(true)
      selectedFees.forEach((fee) => {
        for (let i = 0; i < (fee.selectedCount || 1); i++) {
          feesToCreate.push(
            getConvertedData({
              ...new RegistrationFeesModel({
                userId: registeredUser?.userId || null,
                userName: registeredUser?.userName || null,
                refundId: null,
                refundDocId: null,
                amountRefunded: 0,
                amountScratched: 0,

                paymentStatus: PAYMENT_STATUS.DEFAULT,
                paymentType: PAYMENT_TYPE.CASH,

                selectedUnitsCount: 1,
                registrationDocId: registeredUser?.id || null,
                eventId: registeredUser?.eventId || null,
                eventName: registeredUser?.eventName || null,
                feesItemId: fee.feesItemId,
                feesTitle: fee.feesTitle,
                feesPrice: fee.feesPrice || 0,
                includeSalesTax: publishedEvent?.EventPaymentSettings?.chargeSalesTax || false,
                feesItemCategory: fee.feesCategory || null,

                recipientId: user.id,
                recipientName: getUserFullName(user),
                recipientProfilePicture: user.userProfilePicture || null,

                ownerId: user.id,
                ownerName: getUserFullName(user),
                ownerProfilePicture: user.userProfilePicture || null,
              }).toObject(),
            })
          )
        }
      })

      await helpers.asyncWhileLoop({
        loopCount: feesToCreate.length,
        functionToFire: async (index) => {
          feesToCreate[index].id = (
            await FirestoreService.createItem(
              COLLECTIONS.REGISTRATION_FEES.NAME,
              new RegistrationFeesModel({
                ...feesToCreate[index],
              }).toFirestore()
            )
          ).id
        },
      })

      await httpService({
        url: 'update_recipient',
        data: { registration: registeredUser, payer: user },
        method: 'POST',
      })

      if (onSave) onSave(feesToCreate)

      handleModal?.(false, MODAL_CONSTS.ADD_FEES)
    } catch (error) {
      // ROLLBACK if update_recipient failed
      await helpers.asyncWhileLoop({
        loopCount: feesToCreate.length,
        functionToFire: async (index) => {
          await FirestoreService.deleteItem(
            COLLECTIONS.REGISTRATION_FEES.NAME,
            feesToCreate[index].id!
          )
        },
      })
      console.error('error:', error)
      toastFunctions.error({
        message: 'Add fees failed',
      })
    } finally {
      setButtonLoading(false)
    }
  }

  const onChange = (id: string, value: number) => {
    const ticketsUpdated = fees.map((item) => {
      if (item.feesItemId === id) {
        return { ...item, selectedCount: value || 0 }
      } else {
        return item
      }
    })
    setFees([...ticketsUpdated])
    setSelectedFees([...ticketsUpdated.filter((item) => (item.selectedCount || 0) > 0)])
  }

  const totalSelected = sumBy(selectedFees, (item) => item.selectedCount || 1)

  let totalPrice: string | number = sumBy(
    selectedFees,
    (item) => convertPrice(item.feesPrice || 0) * (item.selectedCount || 1)
  )

  return (
    <MainModal
      title="Add fees"
      show={true}
      type="ADD_FEES"
      size="lg"
      className="flex flex-col flex-1"
      buttons={[
        {
          label: selectedFees?.[0]
            ? `Add ${totalSelected} Fees, ${getFloatPrice(totalPrice / 100)}`
            : 'Add Fees',
          fullWidth: true,
          loading: buttonLoading,
          disabled: buttonLoading || !selectedFees?.[0],
          onClick: onSubmit,
        },
        {
          label: 'Cancel',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitIconThemeColor/50',
          fullWidth: true,
          disabled: buttonLoading,
          onClick: () => handleModal?.(false, MODAL_CONSTS.ADD_FEES),
        },
      ]}
    >
      <div className="flex flex-col flex-1 w-full pt-4 h-full text-SeabiscuitDark200ThemeColor">
        {loading ? (
          <AddItemSkeleton />
        ) : (
          <div className="flex flex-wrap gap-3">
            <AddItemAccordion
              name={FEES_CATEGORY_CONST.STALLS}
              items={getByCategory(fees, FEES_CATEGORY_CONST.STALLS)}
              selectedItems={getByCategory(selectedFees, FEES_CATEGORY_CONST.STALLS)}
              onChange={onChange}
            />
            <AddItemAccordion
              name={FEES_CATEGORY_CONST.BEDDING}
              items={getByCategory(fees, FEES_CATEGORY_CONST.BEDDING)}
              selectedItems={getByCategory(selectedFees, FEES_CATEGORY_CONST.BEDDING)}
              onChange={onChange}
            />
            <AddItemAccordion
              name={FEES_CATEGORY_CONST.FEED}
              items={getByCategory(fees, FEES_CATEGORY_CONST.FEED)}
              selectedItems={getByCategory(selectedFees, FEES_CATEGORY_CONST.FEED)}
              onChange={onChange}
            />
            <AddItemAccordion
              hideRemaining
              name={FEES_CATEGORY_CONST.GOVERNANCE}
              items={getByCategory(fees, FEES_CATEGORY_CONST.GOVERNANCE)}
              selectedItems={getByCategory(selectedFees, FEES_CATEGORY_CONST.GOVERNANCE)}
              onChange={onChange}
            />
            <AddItemAccordion
              name={FEES_CATEGORY_CONST.OTHER}
              items={getByCategory(fees, FEES_CATEGORY_CONST.OTHER)}
              selectedItems={getByCategory(selectedFees, FEES_CATEGORY_CONST.OTHER)}
              onChange={onChange}
            />
          </div>
        )}
      </div>
    </MainModal>
  )
}
